/**
 * Shared caches for workflow node configuration panels.
 * Prevents repeated API calls on every node selection click.
 *
 * Covers:
 *  - /datasources              → used by DuplicateDetectorConfig, DatabaseLookupConfig
 *  - /users/by-role?role=...   → used by ApprovalConfig
 */
import { apiFetch } from '@/api';

const API_BASE_URL = (() => { const base = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, ''); return base.endsWith('/api/v1') ? base : base + '/api/v1'; })();

const getToken = () => {
    try {
        const s = sessionStorage.getItem('access_token');
        if (s) return s;
        const t = localStorage.getItem('token') || localStorage.getItem('authToken');
        if (t) return t;
        const st = localStorage.getItem('auth-storage');
        if (st) return JSON.parse(st).state?.token || null;
    } catch { }
    return null;
};

// ─── Datasources cache ────────────────────────────────────────────────────────
let _dsCache: any[] | null = null;
let _dsInflight: Promise<any[]> | null = null;

export async function fetchDatasources(): Promise<any[]> {
    if (_dsCache !== null) return _dsCache;
    if (_dsInflight) return _dsInflight;
    _dsInflight = apiFetch('/datasources', null, { method: 'GET' })
        .then((d: any) => {
            _dsCache = Array.isArray(d) ? d : [];
            _dsInflight = null;
            return _dsCache!;
        })
        .catch(() => {
            _dsInflight = null;
            return [] as any[];
        });
    return _dsInflight;
}

export function invalidateDatasourceCache(): void {
    _dsCache = null;
    _dsInflight = null;
}

// ─── Users-by-role cache ──────────────────────────────────────────────────────
const _roleCache = new Map<string, any[]>();
const _roleInflight = new Map<string, Promise<any[]>>();

export async function fetchUsersByRole(role: string): Promise<any[]> {
    if (_roleCache.has(role)) return _roleCache.get(role)!;
    if (_roleInflight.has(role)) return _roleInflight.get(role)!;

    const token = getToken();
    const p = fetch(`${API_BASE_URL}/users/by-role?role=${encodeURIComponent(role)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    })
        .then(res => res.json())
        .then(users => {
            const list = Array.isArray(users) ? users : [];
            _roleCache.set(role, list);
            _roleInflight.delete(role);
            return list;
        })
        .catch(() => {
            _roleInflight.delete(role);
            return [] as any[];
        });

    _roleInflight.set(role, p);
    return p;
}

/** Call after user management changes so next selection re-fetches. */
export function invalidateRoleCache(role?: string): void {
    if (role) {
        _roleCache.delete(role);
        _roleInflight.delete(role);
    } else {
        _roleCache.clear();
        _roleInflight.clear();
    }
}
